import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { useSiteMode } from '../auth/SiteModeContext.js';
import { useTenantScope } from '../auth/TenantScopeContext.js';
import { useMasterPerRequestScope } from './masterScopedRequest.js';

/** Shared tenant context for list + detail pages (URL param -> JWT/default tenant). */
export function useTenantScopedPage(fallbackTenantId?: string | null) {
  const { state } = useAuth();
  const siteMode = useSiteMode();
  const { tenantId: defaultTenantId, tenantName } = useTenantScope();
  const [searchParams] = useSearchParams();
  const tenantFromUrl = searchParams.get('tenantId') ?? searchParams.get('tenant');
  const jwtTenantId = state.status === 'authed' ? state.user.tenant_id : null;

  const isMaster =
    state.status === 'authed' &&
    state.user.role === 'master_admin' &&
    state.user.tenant_id == null;
  const installDefaultTenantId =
    isMaster && (siteMode === 'single_tenant' || siteMode === 'multi_family')
      ? defaultTenantId
      : null;

  const effectiveTenantId =
    tenantFromUrl ?? jwtTenantId ?? fallbackTenantId ?? installDefaultTenantId ?? null;
  const hasTenantContext = !!effectiveTenantId;
  const isMasterWithoutContext = isMaster && !hasTenantContext;
  const canLoadData = state.status === 'authed' && (!isMaster || hasTenantContext);
  const canShowTenantActions = state.status === 'authed' && hasTenantContext;
  const canExecuteTenantActions = hasTenantContext;
  const scopeOpts = useMasterPerRequestScope(effectiveTenantId);

  const scopedDetailHref = useCallback(
    (
      basePath: string,
      resourceId: string,
      extraQuery?: Record<string, string>,
      tenantOverride?: string | null,
    ): string | null => {
      const tid = tenantOverride ?? effectiveTenantId;
      if (!tid) return null;
      const q = new URLSearchParams({ tenantId: tid, ...extraQuery });
      const normalized = basePath.replace(/\/$/, '');
      return `${normalized}/${resourceId}?${q.toString()}`;
    },
    [effectiveTenantId],
  );

  return {
    tenantFromUrl,
    effectiveTenantId,
    tenantName,
    scopeOpts,
    isMasterWithoutContext,
    canLoadData,
    hasTenantContext,
    canShowTenantActions,
    canExecuteTenantActions,
    scopedDetailHref,
    isMaster,
  };
}
